import { BodyMedium, BodySmall, BodyXS, Box, Button, Checkbox, CircularProgress, RadioGroup } from "@parspec/pixel";

import { useCompanyInfoQuery } from "src/features/Settings/BranchLocations/queries";
import { VISIBILITY_RADIO_OPTIONS } from "../constant";
import { CompanyLocationResponse } from "src/features/Settings/BranchLocations/queries/apiTypes";
import { LocationVisibilityState } from "../types";

interface LocationVisibilityProps {
    locationVisiblity: LocationVisibilityState;
    setLocationVisibility: React.Dispatch<React.SetStateAction<LocationVisibilityState>>;
    visibilityErr: string;
    setVisibilityErr: React.Dispatch<React.SetStateAction<string>>;
}

export default function LocationVisibility(props: LocationVisibilityProps) {
    const {
        locationVisiblity,
        setLocationVisibility,
        visibilityErr,
        setVisibilityErr,
    } = props;

    const { checkedLocationsMap, visibility } = locationVisiblity;

    const { data: allBranchLocations, isLoading: locationsLoading } = useCompanyInfoQuery();

    const isAllSelected = !!allBranchLocations?.data && checkedLocationsMap.size === allBranchLocations.data.length;

    const toggleSelectAll = () => {
        if (!allBranchLocations?.data) return;
        const newMap: Map<number, CompanyLocationResponse> = isAllSelected
            ? new Map()
            : new Map(allBranchLocations.data.map((company) => [company.id, company]));

        setLocationVisibility(prev => ({ ...prev, checkedLocationsMap: newMap }));
        setVisibilityErr("");
    };

    const toggleCheckbox = (company: CompanyLocationResponse) => {
        setLocationVisibility(prev => {
            const newMap = new Map(prev.checkedLocationsMap);
            if (newMap.has(company.id)) newMap.delete(company.id);
            else newMap.set(company.id, company);
            return { ...prev, checkedLocationsMap: newMap };
        });
        setVisibilityErr("");
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        // on radio change empty the location selected set
        setLocationVisibility({
            visibility: value,
            checkedLocationsMap: new Map(),
        });
        setVisibilityErr("");
    };

    return (
        <Box display="flex" width="100%" flexDirection="column">
            <BodySmall color="secondary.main">Visibility</BodySmall>
            <Box>
                <RadioGroup
                    options={VISIBILITY_RADIO_OPTIONS}
                    value={visibility}
                    onChange={handleRadioChange}
                    name="visiblity"
                    label=""
                />
            </Box>

            {visibility === VISIBILITY_RADIO_OPTIONS[1].value && (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="baseline">
                            <BodyMedium>Location Access</BodyMedium>
                            <BodyXS mt={1} color={'error.main'}>*</BodyXS>
                        </Box>
                        <Box>
                            <Button variant="outlined" color="secondary" size="small" onClick={toggleSelectAll} disabled={locationsLoading}>
                                {isAllSelected ? 'Deselect All' : 'Select All'}
                            </Button>
                        </Box>
                    </Box>
                    <Box
                        my={3}
                        position="relative"
                        maxHeight="194px"
                        bgcolor="neutral.light"
                        p={4}
                        sx={{ overflow: 'hidden', overflowY: 'scroll' }}
                    >
                        {locationsLoading && (
                            <Box position="absolute" top="50%" left="50%" sx={{ transform: 'translate(-50%, -50%)' }}>
                                <CircularProgress size="lg" color={'primary'} thickness={3.6} />
                            </Box>
                        )}
                        <Box>
                            {allBranchLocations?.data?.length !== 0 &&
                                allBranchLocations?.data.map((eachLocationData) => {
                                    return (
                                        <Box key={eachLocationData.id}>
                                            <Checkbox
                                                checked={checkedLocationsMap.has(eachLocationData.id)}
                                                label={<BodySmall>{eachLocationData.name}</BodySmall>}
                                                onChange={() => toggleCheckbox(eachLocationData)}
                                            />
                                        </Box>
                                    );
                                })}
                        </Box>
                    </Box>
                    {!!visibilityErr && <BodyXS color="error.main">{visibilityErr}</BodyXS>}
                </>
            )}
        </Box>
    )
}